// Borde HTTP de /roles contra Core Manage API v1.
//
// El contrato declara estas respuestas como `ApiResponse` sin tipar (`JsonObject`), así que el
// schema está escrito a mano contra la forma observada en el backend real, no generado
// (spec.md §6, response-shapes.md).

import { z } from 'zod';
import apiClient from '@shared/api/http';
import type { RoleId } from '../domain/ids';
import type { RoleSummary, RoleWritePayload } from '../domain/role';
import type { RoleOption } from '../domain/external';
import type { User } from '../domain/user';

const RoleDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  catalogCode: z.string().optional(),
  category: z.string().optional(),
  isSystem: z.boolean().optional(),
  isEditable: z.boolean().optional(),
  isDeletable: z.boolean().optional(),
  // v1 solo publica el recuento; el árbol de permisos por rol ya no se expone (G-13).
  permissionCount: z.number().optional(),
  assignedUserCount: z.number().optional(),
});
type RoleDto = z.infer<typeof RoleDtoSchema>;

function toDomain(dto: RoleDto): RoleSummary {
  return {
    id: dto.id as RoleId,
    roleName: dto.name,
    description: dto.description ?? undefined,
    permissionCount: dto.permissionCount,
    assignedUserCount: dto.assignedUserCount,
  };
}

function toDto(payload: RoleWritePayload) {
  return {
    name: payload.role_name,
    description: payload.description,
  };
}

const RoleUserDtoSchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  userType: z.enum(['internal', 'external']),
  status: z.enum(['active', 'deactivated']),
});

function toUserDomain(dto: z.infer<typeof RoleUserDtoSchema>, roleId: RoleId): User {
  return {
    id: dto.id as User['id'],
    userType: dto.userType === 'internal' ? 'employee' : dto.userType,
    // v1 no devuelve `username` en ningún DTO de lectura (improvements.md G-15).
    username: dto.email,
    firstName: dto.firstName,
    lastName: dto.lastName,
    email: dto.email,
    roleId,
    roleName: '',
  };
}

export const roleRepository = {
  async getAll(): Promise<RoleSummary[]> {
    const { data } = await apiClient.get('/roles');
    return z.array(RoleDtoSchema).parse(data).map(toDomain);
  },

  async getById(id: RoleId): Promise<RoleSummary | null> {
    const { data } = await apiClient.get(`/roles/${id}`);
    if (!data) return null;
    return toDomain(RoleDtoSchema.parse(data));
  },

  async create(payload: RoleWritePayload): Promise<RoleSummary> {
    const { data } = await apiClient.post('/roles', toDto(payload));
    return toDomain(RoleDtoSchema.parse(data));
  },

  // v1 usa PATCH para la actualización parcial de rol (mapping.md §S-3).
  async update(id: RoleId, payload: RoleWritePayload): Promise<RoleSummary> {
    const { data } = await apiClient.patch(`/roles/${id}`, toDto(payload));
    return toDomain(RoleDtoSchema.parse(data));
  },

  // Deja pasar el error del backend tal cual (patrón rack.repository.ts) — su mapeo a mensaje
  // legible ocurre en application/.
  async remove(id: RoleId): Promise<void> {
    await apiClient.delete(`/roles/${id}`);
  },

  // Consumo público (§6/R-5): change-mgmt (ExecutionTrackerPage) lo espera de `iam`.
  async getUsersByRole(id: RoleId): Promise<User[]> {
    const { data } = await apiClient.get(`/roles/${id}/users`);
    return z
      .array(RoleUserDtoSchema)
      .parse(data)
      .map((dto) => toUserDomain(dto, id));
  },
};

/** Mapea roles a las opciones mínimas que consumen los selectores del wizard/detalle. */
export function toRoleOptions(roles: RoleSummary[]): RoleOption[] {
  return roles.map((r) => ({ id: r.id, roleName: r.roleName }));
}
